import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

const PHASES = [
  { name: "Phase 1", desc: "Revenue ≤ ₹50 Cr, Capital ≤ ₹10 Cr", weights: { revenue: 50, capital: 15, tenure: 10, leadership: 10, founder: 15 } },
  { name: "Phase 2", desc: "Revenue ≤ ₹200 Cr, Capital ≤ ₹100 Cr", weights: { revenue: 50, capital: 20, tenure: 10, leadership: 10, founder: 10 } },
  { name: "Phase 3", desc: "Revenue > ₹200 Cr, Capital > ₹100 Cr", weights: { revenue: 55, capital: 20, tenure: 10, leadership: 10, founder: 5 } },
];

export default function AdminConfig() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/config");
    setCfg(data);
  };
  useEffect(() => { load(); }, []);

  if (!cfg) return <div className="eyebrow animate-pulse">Loading…</div>;

  const totalWeights = Object.values(cfg.weights).reduce((a, b) => a + Number(b || 0), 0);

  const update = (path, value) => {
    setCfg((c) => {
      const next = { ...c };
      const keys = path.split(".");
      let ref = next;
      for (let i = 0; i < keys.length - 1; i++) ref = ref[keys[i]];
      ref[keys[keys.length - 1]] = value;
      return { ...next };
    });
  };

  const applyPhase = (p) => {
    setCfg((c) => ({ ...c, phase_name: p.name, weights: { ...p.weights } }));
    toast.success(`${p.name} preset applied (unsaved)`);
  };

  const updateBand = (i, key, value) => {
    setCfg((c) => {
      const bands = [...c.tenure_bands];
      bands[i] = { ...bands[i], [key]: key === "label" ? value : Number(value) };
      return { ...c, tenure_bands: bands };
    });
  };
  const addBand = () => setCfg((c) => ({ ...c, tenure_bands: [...c.tenure_bands, { label: "New", min_years: 0, max_years: 0, points: 0 }] }));
  const delBand = (i) => setCfg((c) => ({ ...c, tenure_bands: c.tenure_bands.filter((_, idx) => idx !== i) }));

  const updateRole = (key, val) => setCfg((c) => ({ ...c, position_points: { ...c.position_points, [key]: Number(val) } }));
  const addRole = () => {
    const name = window.prompt("Leadership role name (e.g. 'Country Practice Leader')");
    if (!name) return;
    setCfg((c) => ({ ...c, position_points: { ...c.position_points, [name]: 0 } }));
  };
  const delRole = (key) => setCfg((c) => {
    const pp = { ...c.position_points };
    delete pp[key];
    return { ...c, position_points: pp };
  });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/config", cfg);
      toast.success("Configuration saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="anim-fade-up max-w-5xl">
      <div className="mb-10">
        <div className="eyebrow mb-2">Engine</div>
        <h1 className="display-serif text-5xl text-atf-brand">Configuration</h1>
        <p className="text-atf-text2 mt-3 max-w-2xl">
          The mechanics behind every rupee distributed. Adjust phase weights, tenure point bands,
          and leadership role values. All future and recomputed distributions will use this.
        </p>
      </div>

      {/* Phase Presets */}
      <Section title="Phase Preset" desc="Apply the prospectus-recommended weight set in one click.">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {PHASES.map((p) => {
            const active = cfg.phase_name === p.name;
            return (
              <button
                key={p.name}
                onClick={() => applyPhase(p)}
                data-testid={`phase-${p.name.replace(/\s+/g, "-").toLowerCase()}`}
                className={`text-left p-5 border transition-colors ${active ? "border-atf-brand bg-atf-brand text-white" : "border-atf-border bg-white hover:bg-atf-subtle"}`}
              >
                <div className={`eyebrow ${active ? "!text-white/70" : ""}`}>{p.name}</div>
                <div className={`text-xs mt-1 ${active ? "text-white/70" : "text-atf-text2"}`}>{p.desc}</div>
                <div className="mt-4 grid grid-cols-5 gap-1 font-mono text-[11px]">
                  {Object.entries(p.weights).map(([k, v]) => (
                    <div key={k} className={`text-center py-1 ${active ? "bg-white/10" : "bg-atf-subtle"}`}>{v}%</div>
                  ))}
                </div>
              </button>
            );
          })}
        </div>
        <div className="mt-6">
          <Field label="Active phase label">
            <input value={cfg.phase_name} onChange={(e) => update("phase_name", e.target.value)} className={INPUT} data-testid="phase-name-input" />
          </Field>
        </div>
      </Section>

      {/* Weights */}
      <Section title="Profit Allocation Weights" desc={`Distributable Pool = Net Profit × (1 - Growth Reserve). Each weight slices that pool. Sum must equal 100% (currently ${totalWeights}%).`}>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {["revenue", "capital", "tenure", "leadership", "founder"].map((k) => (
            <Field key={k} label={k}>
              <div className="relative">
                <input
                  type="number"
                  step="0.5"
                  min={0}
                  max={100}
                  value={cfg.weights[k]}
                  onChange={(e) => update(`weights.${k}`, Number(e.target.value))}
                  className={INPUT + " pr-7"}
                  data-testid={`weight-${k}`}
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-atf-text2 text-xs">%</span>
              </div>
            </Field>
          ))}
        </div>
        {totalWeights !== 100 && (
          <div className="mt-3 text-xs text-atf-negative" data-testid="weights-warning">
            Weights total {totalWeights}% — must be 100% before saving.
          </div>
        )}
      </Section>

      {/* Growth reserve + FX */}
      <Section title="Reserve & FX">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Growth Reserve %">
            <input type="number" step="0.5" min={0} max={100} value={cfg.growth_reserve_pct}
              onChange={(e) => update("growth_reserve_pct", Number(e.target.value))} className={INPUT} data-testid="growth-reserve" />
          </Field>
          <Field label="INR per USD (display only)">
            <input type="number" step="0.5" min={1} value={cfg.inr_per_usd}
              onChange={(e) => update("inr_per_usd", Number(e.target.value))} className={INPUT} data-testid="fx-rate" />
          </Field>
        </div>
      </Section>

      {/* Tenure bands */}
      <Section title="Tenure Point Bands" desc="Points awarded for each band of years served as a partner.">
        <div className="border border-atf-border bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-atf-border">
                <Th>Label</Th><Th align="right">Min years</Th><Th align="right">Max years</Th><Th align="right">Points</Th><Th align="right"></Th>
              </tr>
            </thead>
            <tbody>
              {cfg.tenure_bands.map((b, i) => (
                <tr key={i} className="border-b border-atf-border last:border-b-0">
                  <td className="px-4 py-2"><input value={b.label} onChange={(e) => updateBand(i, "label", e.target.value)} className={INPUT_SM} data-testid={`band-label-${i}`} /></td>
                  <td className="px-4 py-2"><input type="number" value={b.min_years} onChange={(e) => updateBand(i, "min_years", e.target.value)} className={INPUT_SM + " text-right"} data-testid={`band-min-${i}`} /></td>
                  <td className="px-4 py-2"><input type="number" value={b.max_years} onChange={(e) => updateBand(i, "max_years", e.target.value)} className={INPUT_SM + " text-right"} data-testid={`band-max-${i}`} /></td>
                  <td className="px-4 py-2"><input type="number" value={b.points} onChange={(e) => updateBand(i, "points", e.target.value)} className={INPUT_SM + " text-right"} data-testid={`band-points-${i}`} /></td>
                  <td className="px-4 py-2 text-right"><button onClick={() => delBand(i)} className="text-atf-text2 hover:text-atf-negative" data-testid={`band-delete-${i}`}><Trash2 size={14} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="border-t border-atf-border p-3 flex justify-end">
            <button onClick={addBand} className="text-xs uppercase tracking-[0.18em] font-semibold text-atf-brand flex items-center gap-1" data-testid="add-band">
              <Plus size={14} /> Add band
            </button>
          </div>
        </div>
      </Section>

      {/* Leadership */}
      <Section title="Leadership Role Points" desc="Relative weight of each leadership position when allocating the leadership sub-pool.">
        <div className="border border-atf-border bg-white">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-atf-border"><Th>Role</Th><Th align="right">Points</Th><Th align="right"></Th></tr></thead>
            <tbody>
              {Object.entries(cfg.position_points).map(([k, v]) => (
                <tr key={k} className="border-b border-atf-border last:border-b-0">
                  <td className="px-4 py-2 text-sm">{k}</td>
                  <td className="px-4 py-2"><input type="number" value={v} onChange={(e) => updateRole(k, e.target.value)} className={INPUT_SM + " text-right"} data-testid={`role-points-${k.replace(/\s+/g, "-")}`} /></td>
                  <td className="px-4 py-2 text-right"><button onClick={() => delRole(k)} className="text-atf-text2 hover:text-atf-negative" data-testid={`role-delete-${k.replace(/\s+/g, "-")}`}><Trash2 size={14} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="border-t border-atf-border p-3 flex justify-end">
            <button onClick={addRole} className="text-xs uppercase tracking-[0.18em] font-semibold text-atf-brand flex items-center gap-1" data-testid="add-role">
              <Plus size={14} /> Add role
            </button>
          </div>
        </div>
      </Section>

      <div className="sticky bottom-6 mt-12 flex justify-end">
        <button
          onClick={save}
          disabled={saving || totalWeights !== 100}
          data-testid="save-config"
          className="bg-atf-brand text-white px-8 py-3 text-xs uppercase tracking-[0.18em] font-semibold hover:bg-atf-brand2 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
        >
          {saving ? "Saving…" : "Save configuration"}
        </button>
      </div>
    </div>
  );
}

const Section = ({ title, desc, children }) => (
  <section className="mb-10">
    <h2 className="font-serif text-2xl text-atf-brand">{title}</h2>
    {desc && <p className="text-atf-text2 text-sm mt-1 mb-5 max-w-2xl">{desc}</p>}
    {children}
  </section>
);
const Field = ({ label, children }) => (
  <div>
    <label className="eyebrow block mb-2 capitalize">{label}</label>
    {children}
  </div>
);
const Th = ({ children, align = "left" }) => <th className={`px-4 py-3 eyebrow text-${align}`}>{children}</th>;
const INPUT = "w-full border border-atf-border bg-white px-3 py-2.5 text-sm focus:outline-none focus:border-atf-brand focus:ring-1 focus:ring-atf-brand";
const INPUT_SM = "w-full border border-atf-border bg-white px-2 py-1.5 text-sm focus:outline-none focus:border-atf-brand";
